import {
  Box,
  PhoneMockup,
  PhoneMockupLayout,
  SearchInput,
  Text,
} from '@frontend/ui-library';
import { getIdFromUrl } from '@frontend/utils';
import { useEffect } from 'react';
import { BackButton, BillDetailHeader } from '../../components';
import { useWidgetContext } from '../../context/WidgetContext';
import { PageFields_CurrenciesPage_Fragment as CurrenciesPageFragment } from '../../graphql/generated';
import { useRegisterHotjarPage } from '../../hooks';
import { NetworkSelectionBottomSheet } from './components';
import { CurrencyList } from './components/CurrencyList';
import { NotFound } from './components/NotFound';
import PaymentOptionSuggestions from './components/PaymentOptionSuggestions';
import { getListableCurrencies, getNetworks, mapCurrencies } from './utils';
import useUpdateCurrenciesOnExpiration from './hooks/useUpdateCurrenciesOnExpiration';
import { useSearch } from './hooks/useSearch';
import { useCurrencySelection } from './hooks/useCurrencySelection';
import styles from './CurrencySelection.module.css';
import { PaymentMethod } from '../../graphql/customTypes';
import { isOfframpBill } from '../../utils';
import { useFeatureFlag } from '../../context/FeatureFlagContext';

export interface Currency {
  name: string;
  symbol: string;
  blockchains: {
    chainSymbol: string;
    available: boolean;
    reason: string;
  }[];
}

export interface Network {
  gasFee: string;
  network: string;
  symbol: string;
  available: boolean;
  reason: string;
  isLowestFeeNetwork: boolean;
}

export interface CurrencySelectionProps {
  data: CurrenciesPageFragment['pageData'];
  selectedMethod: PaymentMethod;
  onBack: () => void;
  onSelectionError: () => void;
  onSolanaPaySelected: () => void;
}

const CurrencySelection = ({
  data,
  selectedMethod,
  onSelectionError,
  onBack,
  onSolanaPaySelected,
}: CurrencySelectionProps) => {
  const { expiresAt, merchantName, fiatTotal, fiatCurrency, billType } = data;

  const { configureHeader } = useWidgetContext();
  useRegisterHotjarPage('currency-selection');

  const { currencies: notExpiredCurrencies } = useUpdateCurrenciesOnExpiration({
    currencies: data.cryptoCurrencies,
    expiresAt: expiresAt,
  });

  const { featureFlags } = useFeatureFlag();
  const currencies = mapCurrencies(notExpiredCurrencies);
  const listableCurrencies = getListableCurrencies(
    currencies,
    selectedMethod,
    featureFlags
  );

  const {
    searchTerm,
    filteredCurrencies,
    searchHasResults,
    searchIdle,
    setSearchTerm,
    handleSearchChange,
  } = useSearch(listableCurrencies);

  const {
    selectedCurrency,
    isNetworkSelectionOpen,
    loading,
    handleCurrencySelection,
    closeNetworkSelection,
    handleNetworkSelection,
  } = useCurrencySelection({
    onSelectionError,
    onSolanaPaySelected,
    currencies: listableCurrencies,
    selectedMethod,
  });

  const networks = selectedCurrency
    ? getNetworks(listableCurrencies, selectedCurrency)
    : [];

  useEffect(() => {
    configureHeader({
      id: getIdFromUrl(),
      expiresAt,
      allowClosePayment: true,
      merchantName,
      billType,
      onMobileBackButtonClick: () => onBack,
    });
  }, [configureHeader, expiresAt, merchantName, billType]);

  const isOffRamp = isOfframpBill(billType);

  return (
    <PhoneMockupLayout backButton={<BackButton onClick={onBack} />}>
      <PhoneMockup
        header={
          <BillDetailHeader
            amount={fiatTotal}
            currency={fiatCurrency}
            isOffRamp={isOffRamp}
          />
        }
        bottomSheet={
          <NetworkSelectionBottomSheet
            open={isNetworkSelectionOpen}
            loading={loading}
            onNetworkSelect={handleNetworkSelection}
            onRequestClose={closeNetworkSelection}
            selectedCurrency={selectedCurrency || ''}
            networks={networks}
          />
        }
      >
        <Text
          mt="0"
          mb="24"
          as="h1"
          size="big"
          weight="700"
          color="royal-700"
          align="center"
        >
          Select currency
        </Text>
        <div className={styles['search-container']}>
          <SearchInput
            data-test-id="currency-search-input"
            value={searchTerm}
            placeholder="Search"
            onChange={handleSearchChange}
          />
        </div>
        <Box as="div" mt={searchTerm ? '8' : '0'} />
        {(searchHasResults || searchIdle) && (
          <CurrencyList
            currencies={filteredCurrencies}
            disabled={loading}
            handleCurrencySelection={handleCurrencySelection}
          />
        )}
        {!searchHasResults && !searchIdle && (
          <NotFound onSuggestCurrencyClick={() => setSearchTerm('')} />
        )}
        {(searchHasResults || searchIdle) && (
          <Box mt="auto">
            <PaymentOptionSuggestions mt="24" page="currencySelection" />
          </Box>
        )}
      </PhoneMockup>
    </PhoneMockupLayout>
  );
};

export const SectionHeader = ({ children }: { children: React.ReactNode }) => {
  return (
    <Text as="h3" mt="16" mb="0" size="small" weight="600" color="royal-700">
      {children}
    </Text>
  );
};

export default CurrencySelection;
